import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';
import { StateTypes, Utils } from '@ortho-next/nextray-core';
import { BridgeMessages } from '../../../nextray/Core/Bridge';
import { AnatomicalSideLabelPipe, BaseMaLtTableComponent, HighlightCellDirective } from '../../shared';


/**
* Table of deformity measures for Lateral image.
*/
@Component({
	selector: 'ma-lt-table',
	templateUrl: './ma-lt-table.component.html',
	imports: [
		FormsModule,
		NgbCollapseModule,
		ReactiveFormsModule,
		HighlightCellDirective,
		AnatomicalSideLabelPipe
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class MaLtTableComponent extends BaseMaLtTableComponent implements OnInit, OnDestroy {

	isCollapsed = true;

	lateralMeasuresForm?: FormGroup;

	private readonly twoDigitsPattern: RegExp = /^\d$|^[1-9]\d{0,2}$/;


	ngOnInit(): void {
		const validators: ValidatorFn[] = [Validators.required, Validators.pattern(this.twoDigitsPattern), Validators.min(0), Validators.max(99)];
		this.lateralMeasuresForm = new FormGroup({
			'PPTA': new FormControl(this.def_standardAngles_PPTA, validators),
			'ADTA': new FormControl(this.def_standardAngles_ADTA, validators)
		});
		this.canvasSrv.addEventListener('onResult', this.historyUpdate);
	}

	/**
	* Event listener for history update.
	*/
	historyUpdate = (event: { args: keyof BridgeMessages }) => {
		if (event.args == 'historyChanged') {
			this.lateralMeasuresForm!.controls['PPTA'].setValue(this.def_standardAngles_PPTA);
			this.lateralMeasuresForm?.controls['ADTA'].setValue(this.def_standardAngles_ADTA);
		}
	};

	// VALIDATION

	/**
	* Check if PDFA value is valid.
	*/
	get isValidPDFA(): boolean {
		return !this.defLTMeasures?.def_lt_PDFA || Utils.isPaleyAngleValid(this.defLTMeasures.def_lt_PDFA, this.ltAngles.PDFA);
	}

	/**
	* Check if Knee Translation value is valid.
	*/
	get isValidKneeTransl(): boolean {
		return !this.defLTMeasures?.def_lt_kneeTranslation || Utils.isPaleyAngleValid(this.defLTMeasures.def_lt_kneeTranslation, this.ltAngles.KneeTransl);
	}

	/**
	* Check if PPTA value is valid.
	*/
	get isValidPPTA(): boolean {
		return !this.defLTMeasures?.def_lt_PPTA || Utils.isPaleyAngleValid(this.defLTMeasures.def_lt_PPTA, this.ltAngles.PPTA);
	}

	/**
	* Check if ADTA value is valid.
	*/
	get isValidADTA(): boolean {
		return !this.defLTMeasures?.def_lt_ADTA || Utils.isPaleyAngleValid(this.defLTMeasures.def_lt_ADTA, this.ltAngles.ADTA);
	}

	/**
	* Check if a measure is out of range.
	*/
	get isMeasureOutOfRange(): boolean {
		return !this.isValidPDFA || !this.isValidKneeTransl || !this.isValidPPTA || !this.isValidADTA;
	}

	// ANGLES STANDARD

	/**
	* Update PPTA target value.
	*/
	updateStandardPPTA(): void {
		if (this.defLTMeasures?.def_lt_PPTA && this.stateType === StateTypes.deformityAnalysis) {
			if (this.lateralMeasuresForm!.controls['PPTA'].valid) {
				const target = Number.parseInt(this.lateralMeasuresForm!.controls['PPTA'].value);
				this.canvasSrv.dispatch('setStandardAngle_PPTA', target);
			} else {
				this.lateralMeasuresForm!.controls['PPTA'].setValue(this.def_standardAngles_PPTA);
			}
		}
	}

	/**
	* Update ADTA target value.
	*/
	updateStandardADTA(): void {
		if (this.defLTMeasures?.def_lt_ADTA && this.stateType === StateTypes.deformityAnalysis) {
			if (this.lateralMeasuresForm?.controls['ADTA'].valid) {
				const target = Number.parseInt(this.lateralMeasuresForm?.controls['ADTA'].value);
				this.canvasSrv.dispatch('setStandardAngle_ADTA', target);
			} else {
				this.lateralMeasuresForm?.controls['ADTA'].setValue(this.def_standardAngles_ADTA);
			}
		}
	}


	ngOnDestroy(): void {
		this.canvasSrv.removeEventListener('onResult', this.historyUpdate);
	}

}
